<style lang="scss" scoped>
    .about {
        &__container {
            position: fixed;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            overflow: auto;
            z-index: z(modal);
            display: flex;
            flex-direction: column;
            min-height: 100vh;
            background-color: rgba($color-primary, .85);
            transform: translateY(100%);
            transition: transform $transition-short;
            &.active {
                transform: translateY(0);
            }
        }

        &__background {
            z-index: z(modal);
            transform: translateY(100%);
            transition: transform $transition-short;
            overflow: hidden;
            &.active {
                transform: translateY(0);
            }
        }

        &__content {
            flex: 1 0 auto;
            display: flex;
            color: $white;
            padding: 50px 25px 25px;
            flex-wrap: wrap;

            @include above(md) {
                padding: 70px 150px 130px 200px;
                flex-wrap: nowrap;
            }

            @media (min-width: 980px) and (max-width: 1399px) {
                padding: 70px 50px 130px;
            }
        }

        &__main {
            flex: 1 0 100%;

            @include above(md) {
                flex: 1 1 57%;
            }

            &-title {
                @include above(md) {
                    width: 80%;
                    margin: 0 auto;
                }
            }

            &-copy {
                font-weight: 300;
                font-size: toRem(16px);
                line-height: lh(20px, 16px);

                @include above(md) {
                    font-size: toRem(27px);
                    line-height: lh(37px, 27px);
                }
            }
        }

        &__sidebar {
            flex: 1 0 100%;
            padding-top: 28px;

            @include above(md) {
                flex: 1 1 17%;
                margin-left: 150px;
            }
            @media (min-width: 980px) and (max-width: 1399px) {
                margin-left: 80px;
            }
        }

        &__team {
            margin-bottom: 80px;
            &-member {
                display: flex;
                margin-bottom: 30px;
                text-transform: uppercase;

                &:last-of-type {
                    margin-bottom: 0;
                }

                &-image {
                    flex: 0 0 90px;
                    width: 90px;
                    margin-right: 10px;
                    img {
                        width: 100%;
                        height: auto;
                        display: block;
                    }
                }
                &-position {
                    font-weight: bold;
                    color: rgba($white, 0.7);
                    font-size: toRem(12px);
                    line-height: lh(18px, 12px);

                    @include above(md) {
                        font-size: toRem(17px);
                        line-height: lh(23px, 17px);
                    }
                }
                &-name {
                    font-weight: 300;
                    font-size: toRem(16px);
                    line-height: lh(23px, 16px);
                    letter-spacing: 2px;

                    @include above(md) {
                        font-size: toRem(26px);
                        line-height: lh(32px, 26px);
                    }
                }
            }
        }

        &__collaborators {
            &-title {
                letter-spacing: 5px;
                margin-bottom: 40px;
                font-size: toRem(20px);
                line-height: lh(28px, 20px);

                @include above(md) {
                    font-size: toRem(21px);
                    line-height: lh(28px, 21px);
                }
            }
            &-image {
                display: block;
                width: auto;
                height: 35px;
                margin: 0 auto;
            }
        }

        &__main-title,
        &__team-title {
            letter-spacing: 11.1px;
            margin-bottom: 40px;
            font-size: toRem(20px);
            line-height: lh(28px, 20px);

            @include above(md) {
                font-size: toRem(40px);
                line-height: lh(56px, 40px);
            }
        }

        &__footer {
            flex: 0 0 auto;
        }
    }
</style>
<style lang="scss">
    .about {
        &__main {
            &-copy {
                p {
                    margin: 25px 0;
                    @include above(md) {
                        margin: 15px 0;
                    }
                }
                h2 {
                    font-weight: normal;
                    font-size: toRem(18px);
                    line-height: lh(28px, 18px);
                    margin: 20px 0;

                    @include above(md) {
                        font-size: toRem(30px);
                        line-height: lh(40px, 30px);
                        margin: 40px 0;
                    }
                }
                blockquote,
                cite {
                    padding: 0 30px;
                    font-size: toRem(18px);
                }
                cite {
                    margin-top: 10px;
                    display: block;
                }
            }
        }
    }
</style>
<template>
    <div class="about">
        <app-background :progress="4" :static-height="true" class="about__background" :class="{active}" />
        <div class="about__container" :class="{active}">
            <app-header :close="true" @close="$emit('close')" />
            <div class="about__content">
                <div class="about__main">
                    <line-title class="about__main-title" :title="data.about.title" />
                    <div class="about__main-copy" v-html="aboutCopy"></div>
                </div>
                <div class="about__sidebar">
                    <div class="about__team">
                        <line-title class="about__team-title" :title="data.team.title" />
                        <div v-for="(member, i) in data.team.members" :key="`${member.firstName}-${member.lastName}-${i}`" class="about__team-member">
                            <div class="about__team-member-image">
                                <img :src="member.image" :alt="`${member.firstName} ${member.lastName}`">
                            </div>
                            <div class="about__team-member-content">
                                <div class="about__team-member-position">{{ member.position }}</div>
                                <div class="about__team-member-name" v-html="`${member.firstName} ${member.lastName}`">{{ member.firstName }} {{ member.lastName }}</div>
                            </div>
                        </div>
                    </div>
                    <div class="about__collaborators">
                        <line-title class="about__collaborators-title" :title="data.collaborators.title" />
                        <img v-for="(image, i) in data.collaborators.images" :key="`${image}-${i}`" :src="image" class="about__collaborators-image">
                    </div>
                </div>
            </div>
            <app-footer class="about__footer" />
        </div>
    </div>
</template>
<script>
import AppHeader from '@/components/AppHeader'
import AppFooter from '@/components/AppFooter'
import AppBackground from '@/components/AppBackground'
import LineTitle from '@/components/LineTitle'

export default {
    name: 'About',
    props: {
        data: {
            type: Object,
            required: true,
        },
        active: {
            type: Boolean,
            default: false,
        },
    },
    computed: {
        aboutCopy() {
            return this.data.about.copy.replace(/(?:\r\n|\r|\n)/g, '<br>')
        },
    },
    components: {
        AppHeader,
        AppFooter,
        AppBackground,
        LineTitle,
    },
}
</script>
